/**
 * Reflects a point (px, py) across a line defined by ax + by + c = 0.
 * @param {number} px - Point X
 * @param {number} py - Point Y
 * @param {number} a - Line coefficient A
 * @param {number} b - Line coefficient B
 * @param {number} c - Line coefficient C
 * @returns {Object} {x, y} reflected point
 */
export const mirrorPoint = (px, py, a, b, c) => {
    if (px === undefined || px === null || py === undefined || py === null) {
        return { x: px || 0, y: py || 0 };
    }
    const denom = a * a + b * b;
    if (denom === 0) return { x: px, y: py };
    const d = (a * px + b * py + c) / denom;
    return { x: px - 2 * a * d, y: py - 2 * b * d };
};

/**
 * Checks if four points forming a quadrilateral are axis-aligned.
 * Useful for determining if a mirrored rectangle/square is still a rectangle/square.
 * @param {Object} p1, p2, p3, p4 - Four corners {x, y}
 * @param {number} threshold - Tolerance for floating point comparison
 * @returns {boolean} True if axis-aligned
 */
export const isAxisAligned = (p1, p2, p3, p4, threshold = 0.1) => {
    return (Math.abs(p1.x - p4.x) < threshold && Math.abs(p1.y - p2.y) < threshold) ||
        (Math.abs(p1.x - p2.x) < threshold && Math.abs(p1.y - p4.y) < threshold);
};

/**
 * Converts screen coordinates to world coordinates.
 * @param {number} screenX 
 * @param {number} screenY 
 * @param {DOMRect} canvasRect 
 * @param {Object} transform {scale, translateX, translateY}
 * @returns {Object} {x, y} world coordinates
 */
export const screenToWorld = (screenX, screenY, canvasRect, transform) => {
    const canvasX = screenX - canvasRect.left;
    const canvasY = screenY - canvasRect.top;
    const x = (canvasX - transform.translateX) / transform.scale;
    const y = (canvasY - transform.translateY) / transform.scale;
    return { x, y };
};

/**
 * Converts world coordinates to grid coordinates.
 * @param {number} worldX 
 * @param {number} worldY 
 * @param {Object} gridOffset {x, y}
 * @returns {Object} {x, y} grid coordinates
 */
export const worldToGrid = (worldX, worldY, gridOffset) => {
    return {
        x: worldX - gridOffset.x,
        y: worldY - gridOffset.y
    };
};

/**
 * Converts grid coordinates to world coordinates.
 * @param {number} gridX 
 * @param {number} gridY 
 * @param {Object} gridOffset {x, y}
 * @returns {Object} {x, y} world coordinates
 */
export const gridToWorld = (gridX, gridY, gridOffset) => {
    return {
        x: gridX + gridOffset.x,
        y: gridY + gridOffset.y
    };
};
